import axios from 'axios';

const normalizeApiBaseUrl = (value) => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const cleaned = trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
  return cleaned.endsWith('/api') ? cleaned : `${cleaned}/api`;
};

const getApiBaseUrl = () => {
  const configuredUrl = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);

  if (configuredUrl) {
    return configuredUrl;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000/api';
    }
  }

  return 'https://sachin-catering-software.onrender.com/api';
};

const API_BASE_URL = getApiBaseUrl();

let inMemoryToken = null;

export const setAuthToken = (token) => {
  inMemoryToken = token || null;
};

export const clearAuthToken = () => {
  inMemoryToken = null;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Add token to requests when available in memory; cookies are also sent automatically.
api.interceptors.request.use((config) => {
  if (inMemoryToken) {
    config.headers.Authorization = `Bearer ${inMemoryToken}`;
  }
  return config;
});

// Handle token expiry and unauthorized responses
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status } = error.response;

      // Handle 401 Unauthorized (token expired/invalid)
      if (status === 401) {
        clearAuthToken();
        localStorage.removeItem('user');

        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      // Handle 403 Forbidden (insufficient permissions)
      if (status === 403) {
        // Could show a toast notification here
        console.error('Access forbidden: Insufficient permissions');
      }
    }

    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Attendance endpoints
export const attendanceAPI = {
  markToday: (data) => api.post('/attendance/mark', data),
  getMyAttendance: () => api.get('/attendance/my-attendance'),
  getAll: (params) => api.get('/attendance', { params }),
  getSummary: (date) => api.get('/attendance/summary', { params: { date } }),
  createOrUpdate: (data) => api.post('/attendance', data),
  delete: (id) => api.delete(`/attendance/${id}`),
};

// Users endpoints (Owner only)
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Events endpoints
export const eventsAPI = {
  getAll: () => api.get('/events'),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
};

// Expenses endpoints
export const expensesAPI = {
  getAll: () => api.get('/expenses'),
  getByEvent: (eventId) => api.get(`/expenses/event/${eventId}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Employees endpoints
export const employeesAPI = {
  getAll: () => api.get('/employees'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

// Salaries endpoints
export const salariesAPI = {
  getAll: () => api.get('/salaries'),
  getByDate: (date) => api.get(`/salaries/date/${date}`),
  getByEvent: (eventId) => api.get(`/salaries/event/${eventId}`),
  getByMonth: (month) => api.get(`/salaries/month/${month}`),
  create: (data) => api.post('/salaries', data),
  update: (id, data) => api.put(`/salaries/${id}`, data),
  delete: (id) => api.delete(`/salaries/${id}`),
};

// Reports endpoints
export const reportsAPI = {
  getMonthlySummary: (month) => api.get(`/reports/monthly/${month}`),
  getEventProfit: (eventId) => api.get(`/reports/event-profit/${eventId}`),
  getYearlySummary: (year) => api.get(`/reports/yearly/${year}`),
};

// Export endpoints
export const exportAPI = {
  exportEvents: () => api.get('/export/events', { responseType: 'blob' }),
  exportExpenses: () => api.get('/export/expenses', { responseType: 'blob' }),
  exportEmployees: () => api.get('/export/employees', { responseType: 'blob' }),
  exportMonthlyReport: (month) => api.get(`/export/report-monthly/${month}`, { responseType: 'blob' }),
  exportYearlyReport: (year) => api.get(`/export/report-yearly/${year}`, { responseType: 'blob' }),
};

// Bills endpoints
export const billsAPI = {
  getAll: (params) => api.get('/bills', { params }),
  getById: (id) => api.get(`/bills/${id}`),
  create: (data) => api.post('/bills', data),
  update: (id, data) => api.put(`/bills/${id}`, data),
  addPayment: (id, data) => api.post(`/bills/${id}/payments`, data),
  delete: (id) => api.delete(`/bills/${id}`),
};

// Phone book endpoints
export const contactsAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

export default api;
